import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, CircularProgress, Tooltip, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { toast } from 'react-toastify';
import warrantyTicketServices from '../services/warrantyTicketServices';
import WarrantyTicketDialog from './WarrantyTicketDialog';
import WarrantyTicketCreateDialog from './WarrantyTicketCreateDialog';

const priorityColor = { High: '#d32f2f', Medium: '#ed6c02', Low: '#2e7d32' };
const statusColor = { Active: 'primary', 'In Progress': 'warning', Resolved: 'success' };

export default function PropertyWarrantyTickets({ propertyId, onCountChange }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchTickets = () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    warrantyTicketServices.getByProperty(propertyId).then(res => {
      if (res.status === 200) {
        setTickets(res.data || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTickets(); }, [propertyId]);

  useEffect(() => {
    if (onCountChange) onCountChange(tickets.length);
  }, [tickets, onCountChange]);

  const handleCreatePO = async (e, ticketId) => {
    e.stopPropagation();
    try {
      const res = await warrantyTicketServices.createProposal(ticketId);
      if (res.status === 200 || res.status === 201) {
        toast.success(`Proposal ${res.data.publicId} created`);
        navigate(`/proposals/${res.data.proposalId}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error creating proposal';
      toast.error(msg);
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {tickets.length} ticket(s)
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          New Ticket
        </Button>
      </Box>

      {tickets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No warranty tickets for this property
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Ticket #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Issue</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Proposal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => { setSelectedTicketId(ticket.id); setDialogOpen(true); }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{ticket.ticketNumber}</TableCell>
                  <TableCell>
                    <Chip label={ticket.status} color={statusColor[ticket.status] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={ticket.priority} size="small" sx={{ backgroundColor: priorityColor[ticket.priority], color: '#fff', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>{ticket.issueDescription}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ticket.createdBy
                        ? `${ticket.createdBy.firstName || ''} ${ticket.createdBy.lastName || ''}`.trim()
                        : ticket.assignedTech
                          ? `${ticket.assignedTech.firstName || ''} ${ticket.assignedTech.lastName || ''}`.trim()
                          : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(ticket.createdAt)}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Create Service Proposal" arrow>
                      <IconButton size="small" color="primary" onClick={(e) => handleCreatePO(e, ticket.id)}>
                        <PostAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <WarrantyTicketDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticketId={selectedTicketId}
        onUpdate={fetchTickets}
      />

      <WarrantyTicketCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        propertyId={propertyId}
        onCreated={fetchTickets}
      />
    </Box>
  );
}
